import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { type PriceData } from "~/domains/pricing/types/PriceData";
import { type WeightData } from "~/domains/product/types/WeightData";
import { type RootState } from "~/app/store";
import { selectOrderableGroup, selectQuotableGroup } from "~/features/cart/cart/redux/selectors/checkoutPageSelectors";
import {
  selectActiveQuoteAggregates,
  selectActiveQuoteTotalSum,
  selectActiveQuoteTotalTargetSum,
} from "~/features/cart/activeQuote/redux/selectors";
import {
  blurOfferPrice,
  clearQuote,
  deletePackageEntry,
  toggleCommentVisibility,
  updateComment,
  updateDate,
  updateInternalComment,
  updateOfferPrice,
  updateQuantity,
  updateTargetPrice,
} from "~/features/cart/activeQuote/redux/actions";
import {
  selectPackaging,
  toggleShowPackaging,
  updatePriceReference,
} from "~/features/cart/cart/redux/actions/cartPageActions";
import { copyLastPackageEntry } from "~/features/cart/cart/redux/actions/cartPageActions";
import { selectSubtotal, selectTotalQty } from "~/features/cart/cart/redux/selectors/productEntrySelectors";
import { useAppConfig } from "~/app/config/useAppConfig";
import { cartIsDayDisabled } from "~/common/utils/dateUtils";
import {
  buildQuoteTableViewModel,
  type PerEntrySelectionData,
  type ResolvedLineItemGroup,
} from "~/features/cart/activeQuote/utils/buildQuoteTableViewModel";
import type {
  QuotePackageEntryActions,
  QuoteTableViewModel,
} from "~/features/cart/activeQuote/models/QuoteTableViewModel";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import {
  projectDisabledDescriptor,
  quoteDescriptorKey,
  useQuoteLineItemDescriptors,
} from "~/features/cart/activeQuote/hooks/useQuoteLineItemDescriptors";
import type { LineItem } from "~/features/cart/cart/models/CartData";

export interface ActiveQuoteAggregates {
  requestInFlight: boolean;
  accountNumber: string;
  totalWeight: WeightData;
  totalSum: PriceData;
  totalTargetSum: PriceData;
  entryIds: number[];
  validCart: boolean;
  dstError: boolean;
  showOptimePopupOnCheckout: boolean;
  isPnaLoading: boolean;
}

export interface ActiveQuoteViewModel {
  isLoading: boolean;
  accountNumber: string;
  aggregates: ActiveQuoteAggregates;
  editTableViewModel: QuoteTableViewModel;
  reviewOrderablesViewModel: QuoteTableViewModel;
  reviewQuotablesViewModel: QuoteTableViewModel;
  /** Query-assembled descriptors keyed by `${productCode}::${priceReference}` (see useQuoteLineItemDescriptors). */
  descriptorsByKey: ReadonlyMap<string, ProductDescriptor>;
  handleDeleteAll: () => void;
}

/** Group flat package entries by productCode into ResolvedLineItemGroup[]. */
function groupByProduct(lineItems: LineItem[]): ResolvedLineItemGroup[] {
  const order: string[] = [];
  const byProduct = new Map<string, LineItem[]>();
  for (const item of lineItems) {
    if (!byProduct.has(item.productCode)) {
      order.push(item.productCode);
      byProduct.set(item.productCode, []);
    }
    byProduct.get(item.productCode)!.push(item);
  }
  return order.map((productCode) => {
    const items = byProduct.get(productCode)!;
    return { cartEntryId: items[0].id, lineItems: items };
  });
}

export function useActiveQuoteViewModel(): ActiveQuoteViewModel {
  const currencyIso = useAppConfig()?.formatting.currencyIso ?? "";
  const isLoading = useSelector((state: RootState) => state.cartPage.loading);
  const accountNumber = useSelector((state: RootState) => state.cartPage.accountNumber);
  const baseAggregates = useSelector(selectActiveQuoteAggregates);
  const totalSum = useSelector(selectActiveQuoteTotalSum);
  const totalTargetSum = useSelector(selectActiveQuoteTotalTargetSum);
  const orderableGroup = useSelector(selectOrderableGroup);
  const quotableGroup = useSelector(selectQuotableGroup);
  const state = useSelector((s: RootState) => s);
  const packageEntriesById = state.cart.packageEntriesById;
  const dispatch = useDispatch();

  const allLineItems = useMemo(() => Object.values(packageEntriesById) as LineItem[], [packageEntriesById]);

  // PnA now flows through the canonical queries: descriptors per line-item pair, and the loading
  // aggregate derived from those subscriptions' pending states (replaces selectIsPnaLoading).
  const { byKey: descriptorsByKey, isPnaLoading } = useQuoteLineItemDescriptors(allLineItems);

  const handleDeleteAll = useCallback(() => dispatch(clearQuote()), [dispatch]);

  const buildActions = useCallback(
    (packEntryId: number): QuotePackageEntryActions => ({
      isDayDisabled: (date: Date) => cartIsDayDisabled(packageEntriesById[packEntryId], date),
      updateDate: (date: Date) => dispatch(updateDate(date, packEntryId)),
      updateQty: (quantity: number) => dispatch(updateQuantity(quantity, packEntryId)),
      updateComment: (comment: string) => dispatch(updateComment(comment, packEntryId)),
      toggleShowComment: () => dispatch(toggleCommentVisibility(packEntryId)),
      updatePriceReference: (priceRef: string | undefined) =>
        dispatch(updatePriceReference(priceRef || "", packEntryId)),
      removeEntry: () => dispatch(deletePackageEntry(packEntryId)),
      onPackagingClick: () => dispatch(toggleShowPackaging(packEntryId)),
      closePackaging: () => dispatch(toggleShowPackaging(packEntryId, false)),
      selectPackage: (packCode: string) => dispatch(selectPackaging(packEntryId, packCode)),
      updateInternalComment: (comment: string) => dispatch(updateInternalComment(comment, packEntryId)),
      updateOfferPrice: (e) =>
        dispatch(updateOfferPrice(packEntryId, e ? { value: e.floatValue ?? 0, currencyIso } : undefined)),
      blurOfferPrice: () => dispatch(blurOfferPrice(packEntryId)),
      updateTargetPrice: (e) =>
        dispatch(updateTargetPrice(packEntryId, e ? { value: e.floatValue ?? 0, currencyIso } : undefined)),
    }),
    [currencyIso, dispatch, packageEntriesById],
  );

  const buildCopyEntry = useCallback(
    (group: ResolvedLineItemGroup) => () => {
      const lastItem = group.lineItems[group.lineItems.length - 1];
      if (lastItem) {
        dispatch(copyLastPackageEntry(lastItem.id));
      }
    },
    [dispatch],
  );

  const getDescriptor = useCallback(
    (group: ResolvedLineItemGroup) => {
      const firstItem = group.lineItems[0];
      return (
        descriptorsByKey.get(quoteDescriptorKey(firstItem.productCode, firstItem.priceReference)) ??
        projectDisabledDescriptor(state.products.basic[firstItem.productCode], firstItem.priceReference)
      );
    },

    [descriptorsByKey, state],
  );

  const buildGroupViewModel = useCallback(
    (lineItems: LineItem[], mode: "edit" | "view") => {
      const resolvedGroups = groupByProduct(lineItems);
      const perEntryData = new Map<number, PerEntrySelectionData>();
      resolvedGroups.forEach((group) => {
        const productCode = group.lineItems[0].productCode;
        perEntryData.set(group.cartEntryId, {
          totalQuantity: selectTotalQty(state, { productCode }),
          subtotal: selectSubtotal(state, { productCode, isInCheckout: false }),
        });
      });
      return buildQuoteTableViewModel({
        mode,
        resolvedGroups,
        perEntryData,
        buildActions,
        buildCopyEntry,
        getDescriptor,
      });
    },
    [state, buildActions, buildCopyEntry, getDescriptor],
  );

  const editTableViewModel = useMemo(
    () => buildGroupViewModel(allLineItems, "edit"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buildGroupViewModel],
  );

  const reviewOrderablesViewModel = useMemo(() => {
    if (!orderableGroup) {
      return buildGroupViewModel([], "view");
    }
    const groupNumber = orderableGroup.groupNumber;
    const filtered = allLineItems.filter((e) => e.groupNumbers.includes(groupNumber));
    return buildGroupViewModel(filtered, "view");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildGroupViewModel, orderableGroup]);

  const reviewQuotablesViewModel = useMemo(() => {
    if (!quotableGroup) {
      return buildGroupViewModel([], "view");
    }
    const groupNumber = quotableGroup.groupNumber;
    const filtered = allLineItems.filter((e) => e.groupNumbers.includes(groupNumber));
    return buildGroupViewModel(filtered, "view");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildGroupViewModel, quotableGroup]);

  return {
    isLoading,
    accountNumber,
    aggregates: {
      ...baseAggregates,
      totalSum,
      totalTargetSum,
      isPnaLoading,
    },
    editTableViewModel,
    reviewOrderablesViewModel,
    reviewQuotablesViewModel,
    descriptorsByKey,
    handleDeleteAll,
  };
}
